<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  Calendar,
  CalendarCheck2,
  LayoutDashboard,
  LogOut,
  Settings,
  Users,
  BookOpenCheck,
  Layers,
  UserRound,
  MessageCircle,
  Bot,
  ListTree,
  Wifi,
  WifiOff,
  Menu,
  X,
  CreditCard,
  BadgeDollarSign,
  MapPin,
  Moon,
  Sun,
  Link2,
  Globe,
  BarChart3,
  AlertTriangle,
} from 'lucide-vue-next'
import { useDarkMode } from '@/composables/useDarkMode'
import { useWhatsappAccount } from '@/modules/whatsapp/composables/useWhatsappAccount'
import { useStudioChannel } from '@/modules/whatsapp/composables/useStudioChannel'
import { useWhatsappRealtimeStore } from '@/modules/whatsapp/stores/whatsapp-realtime.store'
import { useStudioBroadcastsStore } from '@/modules/dashboard/stores/studio-broadcasts.store'
import { stopAdminImpersonation } from '@/modules/admin/api'
import {
  clearImpersonationStorage,
  isImpersonationExpired,
  readImpersonationStorage,
  restoreAdminTokenFromImpersonation,
} from '@/modules/admin/impersonation'
import type {
  WhatsappConversationEscalatedEvent,
  WhatsappConversationUpdatedEvent,
  WhatsappMessageReceivedEvent,
} from '@/modules/whatsapp/types'

import { useBrandingStore } from '@/stores/branding.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useStudioStore } from '@/modules/studio/stores/studio.store'
import { useAppContextStore } from '@/stores/app-context.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import type { StudioBroadcast } from '@/types'
import BaseButton from '@/components/base/BaseButton.vue'
import { SUPPORT_WHATSAPP } from '@/config/env'

const router = useRouter()
const route = useRoute()
const brandingStore = useBrandingStore()
const authStore = useAuthStore()
const studioStore = useStudioStore()
const contextStore = useAppContextStore()
const subscriptionStore = useSubscriptionStore()
const whatsappRealtimeStore = useWhatsappRealtimeStore()
const studioBroadcastsStore = useStudioBroadcastsStore()

const studioName = computed(() => studioStore.currentStudio?.name ?? 'Studio')
const logoUrl = computed(() => brandingStore.logoUrl)
const studios = computed(() => studioStore.studios)
const isSwitchingStudio = ref(false)
const sidebarOpen = ref(false)

const currentStudioId = computed({
  get: () => studioStore.currentStudioId ?? '',
  set: async (value: string) => {
    studioStore.setCurrentStudio(value)
    isSwitchingStudio.value = true
    await brandingStore.loadBranding(value)
    setTimeout(() => window.location.reload(), 250)
  },
})

// Cerrar sidebar al navegar en mobile
watch(() => route.path, () => {
  sidebarOpen.value = false
})

const logout = async () => {
  await authStore.logout()
  await router.push('/login')
}

const { isDark, toggle: toggleDark } = useDarkMode()
const { account: whatsappAccount, load: loadWhatsappAccount } = useWhatsappAccount()
const isWhatsappOwner = computed(() => ['owner', 'admin'].includes(authStore.role ?? ''))
const isOwner = computed(() => authStore.role === 'owner')
const supportWhatsappNumber = computed(() =>
  (SUPPORT_WHATSAPP ?? '').replace(/\D/g, ''),
)
const showSupportWhatsappButton = computed(() =>
  ['owner', 'admin'].includes(authStore.role ?? '') && supportWhatsappNumber.value.length > 0,
)
const supportWhatsappHref = computed(() => {
  if (!showSupportWhatsappButton.value) return '#'
  const supportMessage = `Hola, necesito ayuda con FlowEstudio. Soy el studio: ${studioName.value}`
  return `https://wa.me/${supportWhatsappNumber.value}?text=${encodeURIComponent(supportMessage)}`
})
const canUseWhatsappRealtime = computed(() =>
  ['owner', 'admin', 'staff', 'coach'].includes(authStore.role ?? '') &&
  Boolean(authStore.token) &&
  Boolean(studioStore.currentStudioId),
)
const activeConversationId = computed<number | null>(() => {
  if (route.name !== 'whatsapp-conversation-detail') return null
  const rawId = route.params.id
  const parsed = Number(Array.isArray(rawId) ? rawId[0] : rawId)
  return Number.isFinite(parsed) ? parsed : null
})
const whatsappUnreadCount = computed(() => whatsappRealtimeStore.unreadCount)
const whatsappEscalationCount = computed(() => whatsappRealtimeStore.escalationCount)
const hasWhatsappEscalations = computed(() => whatsappEscalationCount.value > 0)
const showWhatsappBadge = computed(() =>
  whatsappUnreadCount.value > 0 || hasWhatsappEscalations.value,
)
const whatsappBadgeLabel = computed(() => {
  if (whatsappUnreadCount.value > 0) {
    return whatsappUnreadCount.value > 99 ? '99+' : String(whatsappUnreadCount.value)
  }
  return '!'
})
const escalationAudio = ref<HTMLAudioElement | null>(null)
const escalationNotices = computed(() => whatsappRealtimeStore.escalationNotices)
const showBroadcastsPanel = ref(false)
const dismissingBroadcastId = ref<number | null>(null)
const impersonation = ref(readImpersonationStorage())
const stoppingImpersonation = ref(false)
let impersonationTimerId: number | null = null

const showImpersonationBanner = computed(() =>
  Boolean(impersonation.value) && !isImpersonationExpired(impersonation.value?.expiresAt),
)
const latestUnreadBroadcast = computed(() => studioBroadcastsStore.latestUnread)
const unreadBroadcasts = computed(() => studioBroadcastsStore.unread)
const hasMoreUnreadBroadcasts = computed(() => studioBroadcastsStore.unreadCount > 1)

const broadcastSeverityClass = (severity: StudioBroadcast['severity']) => {
  if (severity === 'critical') return 'layout-broadcast-banner--critical'
  if (severity === 'warning') return 'layout-broadcast-banner--warning'
  return 'layout-broadcast-banner--info'
}

const broadcastSeverityIconClass = (severity: StudioBroadcast['severity']) => {
  if (severity === 'critical') return 'layout-broadcast-banner__icon--critical'
  if (severity === 'warning') return 'layout-broadcast-banner__icon--warning'
  return 'layout-broadcast-banner__icon--info'
}

const formatBroadcastDate = (value: string | null): string => {
  if (!value) return 'Sin fecha de publicación'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'Sin fecha de publicación'
  return parsed.toLocaleString('es-MX')
}

const loadStudioBroadcasts = async () => {
  if (!isOwner.value) {
    studioBroadcastsStore.resetForStudio()
    return
  }
  const studioId = Number(studioStore.currentStudioId)
  if (!Number.isFinite(studioId) || studioId <= 0) {
    studioBroadcastsStore.resetForStudio()
    return
  }
  await studioBroadcastsStore.loadUnreadOnce(studioId)
}

const dismissBroadcast = async (id: number) => {
  dismissingBroadcastId.value = id
  await studioBroadcastsStore.markRead(id)
  dismissingBroadcastId.value = null
  if (studioBroadcastsStore.unreadCount === 0) {
    showBroadcastsPanel.value = false
  }
}

const openBroadcastsPanel = () => {
  showBroadcastsPanel.value = true
}

const closeBroadcastsPanel = () => {
  showBroadcastsPanel.value = false
}

const markAllBroadcastsAsRead = async () => {
  await studioBroadcastsStore.markAllRead()
  showBroadcastsPanel.value = false
}

const completeImpersonationStop = (redirect = true) => {
  const current = readImpersonationStorage()
  const returnPath = current?.returnPath ?? '/admin/studios'
  const restoredAdminToken = restoreAdminTokenFromImpersonation()
  clearImpersonationStorage()
  impersonation.value = null

  if (!restoredAdminToken) return
  authStore.token = restoredAdminToken
  authStore.user = null
  if (redirect) {
    window.location.assign(returnPath)
  }
}

const syncImpersonationState = () => {
  const current = readImpersonationStorage()
  if (!current) {
    impersonation.value = null
    return
  }
  if (isImpersonationExpired(current.expiresAt)) {
    completeImpersonationStop(true)
    return
  }
  impersonation.value = current
}

const stopImpersonationSession = async () => {
  const current = readImpersonationStorage()
  if (!current) {
    impersonation.value = null
    return
  }
  if (isImpersonationExpired(current.expiresAt)) {
    completeImpersonationStop(true)
    return
  }

  stoppingImpersonation.value = true
  try {
    await stopAdminImpersonation(current.token)
  } catch {
    // Si el stop falla por token inválido o ya expirado, igual restauramos la sesión admin.
  } finally {
    stoppingImpersonation.value = false
    completeImpersonationStop(true)
  }
}

const playEscalationSound = () => {
  const audio = escalationAudio.value
  if (!audio) return
  audio.currentTime = 0
  void audio.play().catch(() => {
    // Algunos navegadores bloquean autoplay hasta interacción del usuario.
  })
}

const handleStudioMessageReceived = (event: WhatsappMessageReceivedEvent) => {
  if (!canUseWhatsappRealtime.value) return
  const isInbound = event.direction === 'inbound' || event.sender_type === 'contact'
  if (!isInbound) return

  if (activeConversationId.value === event.conversation_id) {
    whatsappRealtimeStore.markConversationRead(event.conversation_id)
    return
  }
  whatsappRealtimeStore.markConversationUnread(event.conversation_id)
}

const handleStudioEscalation = (event: WhatsappConversationEscalatedEvent) => {
  if (!canUseWhatsappRealtime.value) return
  whatsappRealtimeStore.addEscalation(event.conversation_id, event.escalated_at)

  if (activeConversationId.value === event.conversation_id) {
    whatsappRealtimeStore.markConversationRead(event.conversation_id)
    return
  }

  playEscalationSound()
}

const handleStudioConversationUpdated = (event: WhatsappConversationUpdatedEvent) => {
  if (!canUseWhatsappRealtime.value) return
  if (event.status === 'closed' || event.mode === 'bot') {
    whatsappRealtimeStore.clearEscalationByConversation(event.conversation_id)
  }
}

useStudioChannel({
  onMessageReceived: handleStudioMessageReceived,
  onConversationEscalated: handleStudioEscalation,
  onConversationUpdated: handleStudioConversationUpdated,
})

const attendEscalation = async (noticeId: number, conversationId: number) => {
  whatsappRealtimeStore.dismissEscalationNotice(noticeId)
  whatsappRealtimeStore.markConversationRead(conversationId)
  await router.push({ name: 'whatsapp-conversation-detail', params: { id: conversationId } })
}

const dismissEscalation = (noticeId: number) => {
  whatsappRealtimeStore.dismissEscalationNotice(noticeId)
}

// Header billing alert: past_due o cancelación programada activa
const showBillingAlert = computed(() =>
  subscriptionStore.isPastDue ||
  subscriptionStore.isUnpaid ||
  subscriptionStore.isCancellationScheduled,
)
const billingAlertClass = computed(() =>
  subscriptionStore.isPastDue || subscriptionStore.isUnpaid
    ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400'
    : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
)
const billingAlertLabel = computed(() => {
  if (subscriptionStore.isPastDue || subscriptionStore.isUnpaid) return 'Pago vencido'
  if (subscriptionStore.isCancellationScheduled) return 'Cancelación programada'
  return ''
})

onMounted(() => {
  if (isWhatsappOwner.value) loadWhatsappAccount()
  subscriptionStore.fetchSubscription()
  void loadStudioBroadcasts()
  escalationAudio.value = new Audio('/sounds/whatsapp-escalation.mp3')
  escalationAudio.value.preload = 'auto'
  syncImpersonationState()
  impersonationTimerId = window.setInterval(syncImpersonationState, 30_000)
})

onUnmounted(() => {
  if (impersonationTimerId !== null) {
    window.clearInterval(impersonationTimerId)
    impersonationTimerId = null
  }
})

watch(
  activeConversationId,
  (conversationId) => {
    if (conversationId == null) return
    whatsappRealtimeStore.markConversationRead(conversationId)
  },
  { immediate: true },
)

watch(
  () => studioStore.currentStudioId,
  (newStudioId, oldStudioId) => {
    if (!newStudioId || newStudioId === oldStudioId) return
    if (oldStudioId && newStudioId !== oldStudioId) {
      whatsappRealtimeStore.reset()
    }
    studioBroadcastsStore.resetForStudio()
    void loadStudioBroadcasts()
  },
)
</script>

<template>
  <div class="min-h-screen bg-bg text-text">
    <div v-if="showImpersonationBanner" class="impersonation-banner">
      <span>
        Estas viendo este studio como super admin.
        <strong>{{ impersonation?.studioName }}</strong>
      </span>
      <button
        class="impersonation-banner__btn"
        :disabled="stoppingImpersonation"
        @click="stopImpersonationSession"
      >
        {{ stoppingImpersonation ? 'Terminando...' : 'Terminar sesion' }}
      </button>
    </div>
    <div v-if="showImpersonationBanner" class="impersonation-spacer" />

    <div class="flex min-h-screen">
      <!-- Studio switch overlay -->
      <div v-if="isSwitchingStudio" class="studio-switch-overlay">
        <div class="studio-switch-card">
          <div class="studio-switch-spinner" />
          <span>Cargando...</span>
        </div>
      </div>

      <!-- Backdrop mobile -->
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-20 bg-black/40 lg:hidden"
        @click="sidebarOpen = false"
      />

      <!-- Sidebar -->
      <aside
        class="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-border bg-surface px-4 py-6 transition-transform duration-200 lg:static lg:translate-x-0"
        :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
      >
        <!-- Logo + close button mobile -->
        <div class="mb-8 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <img v-if="logoUrl" :src="logoUrl" alt="Logo" class="h-9 w-9 rounded-full object-cover" />
            <div v-else class="flex h-9 w-9 items-center justify-center rounded-full bg-primary/20">
              <span class="text-xs font-bold text-primary">F</span>
            </div>
            <div>
              <p class="text-sm font-semibold text-text">{{ studioName }}</p>
              <p class="text-xs text-muted">FlowEstudio</p>
            </div>
          </div>
          <button
            class="rounded-brand p-1 text-muted hover:bg-primary/10 lg:hidden"
            @click="sidebarOpen = false"
          >
            <X class="h-4 w-4" />
          </button>
        </div>

        <nav class="flex-1 space-y-1 text-sm">
          <RouterLink
            to="/"
            class="nav-link"
            active-class="nav-link--active"
          >
            <LayoutDashboard class="h-4 w-4 shrink-0" />
            Dashboard
          </RouterLink>
          <RouterLink
            to="/dashboard/insights"
            class="nav-link"
            active-class="nav-link--active"
          >
            <BarChart3 class="h-4 w-4 shrink-0" />
            Insights
          </RouterLink>
          <RouterLink
            to="/students"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Users class="h-4 w-4 shrink-0" />
            Clientes
          </RouterLink>
          <RouterLink
            to="/scheduling/today"
            class="nav-link"
            active-class="nav-link--active"
          >
            <CalendarCheck2 class="h-4 w-4 shrink-0" />
            Agenda
          </RouterLink>
          <RouterLink
            to="/scheduling/class-sessions"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Calendar class="h-4 w-4 shrink-0" />
            Studios / Clases
          </RouterLink>
          <RouterLink
            to="/reservations"
            class="nav-link"
            active-class="nav-link--active"
          >
            <BookOpenCheck class="h-4 w-4 shrink-0" />
            Reservas
          </RouterLink>
          <RouterLink
            to="/products"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Layers class="h-4 w-4 shrink-0" />
            Productos
          </RouterLink>
          <RouterLink
            to="/coaches"
            class="nav-link"
            active-class="nav-link--active"
          >
            <UserRound class="h-4 w-4 shrink-0" />
            Coaches
          </RouterLink>
          <RouterLink
            to="/locations"
            class="nav-link"
            active-class="nav-link--active"
          >
            <MapPin class="h-4 w-4 shrink-0" />
            Sedes
          </RouterLink>
          <RouterLink
            to="/scheduling/class-types"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Layers class="h-4 w-4 shrink-0" />
            Tipos de clase
          </RouterLink>
          <RouterLink
            to="/settings/studio"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Settings class="h-4 w-4 shrink-0" />
            Configuración
          </RouterLink>
          <!-- Pagos section -->
          <div class="pb-1 pt-4">
            <span class="block px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted">Pagos</span>
          </div>
          <RouterLink
            to="/payments/charge"
            class="nav-link"
            active-class="nav-link--active"
          >
            <BadgeDollarSign class="h-4 w-4 shrink-0" />
            Cobrar alumno
          </RouterLink>
          <RouterLink
            to="/payments/gateways"
            class="nav-link"
            active-class="nav-link--active"
          >
            <CreditCard class="h-4 w-4 shrink-0" />
            Métodos de cobro
          </RouterLink>

          <!-- WhatsApp section -->
          <div class="pb-1 pt-4">
            <div class="flex items-center justify-between px-3 pb-1">
              <span class="text-[10px] font-semibold uppercase tracking-widest text-muted">WhatsApp</span>
              <span
                v-if="isWhatsappOwner"
                class="h-2 w-2 rounded-full"
                :class="{
                  'bg-emerald-500': whatsappAccount?.is_active,
                  'bg-rose-500': whatsappAccount?.status === 'error',
                  'bg-amber-400': whatsappAccount && !whatsappAccount.is_active && whatsappAccount.status !== 'error',
                  'bg-border': !whatsappAccount,
                }"
                :title="whatsappAccount?.is_active ? 'Activa' : whatsappAccount?.status === 'error' ? 'Error de credenciales' : whatsappAccount ? 'Pendiente' : 'Sin cuenta'"
              />
            </div>
          </div>

          <RouterLink
            to="/whatsapp/conversations"
            class="nav-link"
            active-class="nav-link--active"
          >
            <span class="relative inline-flex items-center">
              <MessageCircle class="h-4 w-4 shrink-0" />
              <span
                v-if="showWhatsappBadge"
                class="whatsapp-nav-badge"
                :class="hasWhatsappEscalations ? 'whatsapp-nav-badge--critical' : 'whatsapp-nav-badge--default'"
              >
                {{ whatsappBadgeLabel }}
              </span>
            </span>
            <span>Conversaciones</span>
          </RouterLink>

          <template v-if="isWhatsappOwner">
            <RouterLink
              to="/whatsapp/account"
              class="nav-link"
              active-class="nav-link--active"
            >
              <component
                :is="whatsappAccount?.is_active ? Wifi : WifiOff"
                class="h-4 w-4 shrink-0"
                :class="whatsappAccount?.is_active ? 'text-emerald-500' : 'text-amber-500'"
              />
              Cuenta WhatsApp
            </RouterLink>

            <RouterLink
              to="/whatsapp/bot-config"
              class="nav-link"
              active-class="nav-link--active"
            >
              <Bot class="h-4 w-4 shrink-0" />
              Config. del bot
            </RouterLink>

            <RouterLink
              to="/whatsapp/bot-intents"
              class="nav-link"
              active-class="nav-link--active"
            >
              <ListTree class="h-4 w-4 shrink-0" />
              Bot Intents
            </RouterLink>
          </template>

          <!-- Integraciones section -->
          <div class="pb-1 pt-4">
            <span class="block px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted">Integraciones</span>
          </div>
          <RouterLink
            to="/integrations/wellhub"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Link2 class="h-4 w-4 shrink-0" />
            Wellhub
          </RouterLink>
          <RouterLink
            to="/integrations/totalpass"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Link2 class="h-4 w-4 shrink-0" />
            TotalPass
          </RouterLink>
          <RouterLink
            to="/integrations/fitpass"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Link2 class="h-4 w-4 shrink-0" />
            Fitpass
          </RouterLink>
          <RouterLink
            to="/integrations/availability"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Calendar class="h-4 w-4 shrink-0" />
            Disponibilidad externa
          </RouterLink>
          <RouterLink
            to="/integrations/incidents"
            class="nav-link"
            active-class="nav-link--active"
          >
            <BarChart3 class="h-4 w-4 shrink-0" />
            Incidencias
          </RouterLink>
          <RouterLink
            to="/integrations/logs"
            class="nav-link"
            active-class="nav-link--active"
          >
            <BarChart3 class="h-4 w-4 shrink-0" />
            Logs
          </RouterLink>
          <RouterLink
            to="/external-reservations"
            class="nav-link"
            active-class="nav-link--active"
          >
            <Globe class="h-4 w-4 shrink-0" />
            Reservas externas
          </RouterLink>
        </nav>
      </aside>

      <!-- Main content -->
      <div class="flex min-w-0 flex-1 flex-col lg:ml-0">
        <header class="flex items-center justify-between border-b border-border bg-surface px-4 py-3 lg:px-6 lg:py-4">
          <div class="flex items-center gap-3">
            <!-- Hamburger (mobile only) -->
            <button
              class="rounded-brand p-1.5 text-muted hover:bg-primary/10 lg:hidden"
              @click="sidebarOpen = true"
            >
              <Menu class="h-5 w-5" />
            </button>
            <div>
              <p class="hidden text-xs text-muted lg:block">Studio activo</p>
              <div class="flex items-center gap-2">
                <h1 class="text-base font-semibold lg:text-lg">{{ studioName }}</h1>
                <select
                  v-if="studios.length > 1"
                  v-model="currentStudioId"
                  class="rounded-brand border border-border bg-surface px-2 py-1 text-xs text-text"
                >
                  <option v-for="studio in studios" :key="studio.id" :value="studio.id.toString()">
                    {{ studio.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 lg:gap-3">
            <!-- Trial badge -->
            <div
              v-if="contextStore.isTrial"
              class="hidden rounded-full border border-amber-500 bg-amber-500 px-3 py-1 text-xs font-semibold text-white sm:block dark:border-amber-600 dark:bg-amber-600"
            >
              <template v-if="contextStore.trialDaysRemaining !== null">
                Prueba · {{ contextStore.trialDaysRemaining }}d
              </template>
              <template v-else>Prueba gratuita</template>
            </div>

            <!-- Billing alert badge (past_due / cancellation scheduled) -->
            <RouterLink
              v-if="showBillingAlert"
              to="/settings/billing"
              class="hidden rounded-full border px-3 py-1 text-xs font-medium sm:block"
              :class="billingAlertClass"
            >
              {{ billingAlertLabel }}
            </RouterLink>
            <button
              class="rounded-brand p-1.5 text-muted hover:bg-primary/10 transition-colors"
              :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
              @click="toggleDark"
            >
              <Sun v-if="isDark" class="h-4 w-4" />
              <Moon v-else class="h-4 w-4" />
            </button>
            <BaseButton variant="ghost" :left-icon="LogOut" @click="logout">
              <span class="hidden sm:inline">Salir</span>
            </BaseButton>
          </div>
        </header>

        <div
          v-if="isOwner && latestUnreadBroadcast"
          class="layout-broadcast-banner"
          :class="broadcastSeverityClass(latestUnreadBroadcast.severity)"
        >
          <AlertTriangle
            class="h-4 w-4 shrink-0"
            :class="broadcastSeverityIconClass(latestUnreadBroadcast.severity)"
          />
          <div class="layout-broadcast-banner__content">
            <p class="layout-broadcast-banner__title">{{ latestUnreadBroadcast.title }}</p>
            <p class="layout-broadcast-banner__body">{{ latestUnreadBroadcast.body }}</p>
            <button
              v-if="hasMoreUnreadBroadcasts"
              class="layout-broadcast-banner__link"
              type="button"
              @click="openBroadcastsPanel"
            >
              Ver todos
            </button>
          </div>
          <button
            class="layout-broadcast-banner__close"
            type="button"
            :disabled="dismissingBroadcastId === latestUnreadBroadcast.id"
            @click="dismissBroadcast(latestUnreadBroadcast.id)"
          >
            <X class="h-4 w-4" />
          </button>
        </div>

        <main class="flex-1 bg-bg px-4 py-4 lg:px-6 lg:py-6">
          <slot />
        </main>

        <div
          v-if="showBroadcastsPanel && unreadBroadcasts.length"
          class="layout-broadcasts-panel-overlay"
          @click.self="closeBroadcastsPanel"
        >
          <div class="layout-broadcasts-panel">
            <div class="layout-broadcasts-panel__header">
              <h3>Avisos del sistema</h3>
              <div class="layout-broadcasts-panel__header-actions">
                <button
                  class="layout-broadcasts-panel__mark-all"
                  type="button"
                  @click="markAllBroadcastsAsRead"
                >
                  Marcar todos como leídos
                </button>
                <button class="layout-broadcasts-panel__close" type="button" @click="closeBroadcastsPanel">
                  <X class="h-4 w-4" />
                </button>
              </div>
            </div>
            <div class="layout-broadcasts-panel__list">
              <article
                v-for="item in unreadBroadcasts"
                :key="item.id"
                class="layout-broadcast-item"
                :class="broadcastSeverityClass(item.severity)"
              >
                <p class="layout-broadcast-item__title">{{ item.title }}</p>
                <p class="layout-broadcast-item__body">{{ item.body }}</p>
                <div class="layout-broadcast-item__meta">
                  <span>{{ formatBroadcastDate(item.published_at) }}</span>
                  <button
                    class="layout-broadcast-item__read"
                    type="button"
                    :disabled="dismissingBroadcastId === item.id"
                    @click="dismissBroadcast(item.id)"
                  >
                    Marcar leído
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>

        <TransitionGroup
          name="escalation-toast"
          tag="div"
          class="escalation-toast-stack"
          :class="{ 'escalation-toast-stack--with-support': showSupportWhatsappButton }"
        >
          <article
            v-for="notice in escalationNotices"
            :key="notice.id"
            class="escalation-toast"
          >
            <div class="escalation-toast__title">
              Conversación escalada a humano
            </div>
            <div class="escalation-toast__subtitle">
              Conversación #{{ notice.conversationId }}
            </div>
            <div class="escalation-toast__actions">
              <button
                type="button"
                class="escalation-toast__btn escalation-toast__btn--primary"
                @click="attendEscalation(notice.id, notice.conversationId)"
              >
                Atender
              </button>
              <button
                type="button"
                class="escalation-toast__btn escalation-toast__btn--ghost"
                @click="dismissEscalation(notice.id)"
              >
                Descartar
              </button>
            </div>
          </article>
        </TransitionGroup>

        <a
          v-if="showSupportWhatsappButton"
          :href="supportWhatsappHref"
          target="_blank"
          rel="noopener noreferrer"
          class="support-whatsapp-fab"
          title="¿Necesitas ayuda?"
          aria-label="Abrir soporte por WhatsApp"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" class="support-whatsapp-icon">
            <path d="M13.601 2.326A7.85 7.85 0 0 0 8.005 0C3.58 0 .008 3.56.008 7.984a7.9 7.9 0 0 0 1.202 4.212L0 16l3.873-1.016a7.96 7.96 0 0 0 4.132 1.125h.003c4.425 0 8-3.56 8.002-7.984a7.95 7.95 0 0 0-2.409-5.799zM8.01 14.73h-.002a6.57 6.57 0 0 1-3.35-.92l-.24-.144-2.3.603.614-2.24-.156-.23a6.56 6.56 0 0 1-1.007-3.505c.002-3.623 2.958-6.57 6.59-6.57a6.54 6.54 0 0 1 4.66 1.93 6.56 6.56 0 0 1 1.923 4.657c-.002 3.624-2.957 6.57-6.59 6.57zm3.615-4.98c-.197-.1-1.17-.58-1.35-.647-.18-.066-.312-.099-.444.1-.132.197-.51.646-.624.78-.114.132-.229.149-.426.05-.197-.099-.833-.307-1.586-.98-.586-.522-.981-1.168-1.096-1.365-.114-.198-.012-.305.087-.404.09-.09.197-.23.296-.346.099-.115.132-.197.198-.33.066-.132.033-.248-.017-.347-.05-.099-.444-1.064-.608-1.46-.16-.387-.323-.335-.444-.341a7.4 7.4 0 0 0-.379-.006c-.132 0-.347.05-.528.248-.181.197-.691.676-.691 1.65s.708 1.916.807 2.049c.099.132 1.394 2.128 3.379 2.986.472.204.84.326 1.127.418.474.151.905.13 1.246.079.38-.057 1.17-.478 1.336-.939.165-.462.165-.858.116-.94-.05-.083-.181-.132-.379-.231z" />
          </svg>
          <span class="support-whatsapp-tooltip" role="tooltip">¿Necesitas ayuda?</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: var(--radius);
  padding: 7px 12px;
  color: var(--text);
  transition: background 0.15s;
}

.nav-link:hover {
  background: color-mix(in oklab, var(--primary), transparent 90%);
}

.nav-link--active {
  background: color-mix(in oklab, var(--primary), transparent 88%);
  color: var(--primary);
  font-weight: 600;
}

.studio-switch-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(1px);
  display: grid;
  place-items: center;
  z-index: 60;
}

.studio-switch-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
}

.studio-switch-spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid color-mix(in oklab, var(--border), transparent 30%);
  border-top-color: var(--primary);
  animation: studioSwitchSpin 0.9s linear infinite;
}

.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 80;
  min-height: 42px;
  background: #fef3c7;
  color: #92400e;
  border-bottom: 1px solid #fcd34d;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 14px;
  font-size: 0.78rem;
  font-weight: 600;
}

.impersonation-banner__btn {
  border: 1px solid #f59e0b;
  background: #fbbf24;
  color: #78350f;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 5px 10px;
}

.impersonation-banner__btn:disabled {
  opacity: 0.65;
}

.impersonation-spacer {
  height: 42px;
}

.layout-broadcast-banner {
  margin: 10px 16px 0;
  border-radius: 12px;
  border: 1px solid transparent;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.layout-broadcast-banner--info {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.layout-broadcast-banner--warning {
  background: #fefce8;
  border-color: #fde68a;
}

.layout-broadcast-banner--critical {
  background: #fef2f2;
  border-color: #fecaca;
}

.layout-broadcast-banner__icon--info { color: #2563eb; }
.layout-broadcast-banner__icon--warning { color: #a16207; }
.layout-broadcast-banner__icon--critical { color: #b91c1c; }

.layout-broadcast-banner__content {
  flex: 1;
  min-width: 0;
}

.layout-broadcast-banner__title {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text);
}

.layout-broadcast-banner__body {
  margin-top: 2px;
  font-size: 0.76rem;
  color: var(--text);
  line-height: 1.35;
}

.layout-broadcast-banner__link {
  margin-top: 6px;
  border: none;
  background: transparent;
  color: var(--primary);
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
}

.layout-broadcast-banner__close {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--muted-text);
  padding: 5px;
  cursor: pointer;
}

.layout-broadcasts-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 66;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 12px;
}

.layout-broadcasts-panel {
  width: min(720px, 100%);
  max-height: min(80vh, 760px);
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: flex;
  flex-direction: column;
}

.layout-broadcasts-panel__header {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.layout-broadcasts-panel__header h3 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
}

.layout-broadcasts-panel__header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.layout-broadcasts-panel__mark-all {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.74rem;
  font-weight: 700;
  padding: 6px 8px;
  cursor: pointer;
}

.layout-broadcasts-panel__close {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--muted-text);
  padding: 5px;
  cursor: pointer;
}

.layout-broadcasts-panel__list {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
}

.layout-broadcast-item {
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 10px;
}

.layout-broadcast-item__title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text);
}

.layout-broadcast-item__body {
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--text);
}

.layout-broadcast-item__meta {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.7rem;
  color: var(--muted-text);
}

.layout-broadcast-item__read {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.72rem;
  font-weight: 600;
  padding: 5px 8px;
  cursor: pointer;
}

.whatsapp-nav-badge {
  position: absolute;
  top: -8px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-size: 10px;
  line-height: 1;
  color: #fff;
  font-weight: 700;
}

.whatsapp-nav-badge--default {
  background: color-mix(in oklab, var(--primary), black 8%);
}

.whatsapp-nav-badge--critical {
  background: #dc2626;
}

.escalation-toast-stack {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 65;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.escalation-toast-stack--with-support {
  bottom: 92px;
}

.escalation-toast {
  width: min(360px, calc(100vw - 32px));
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #881337;
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.14);
  pointer-events: auto;
}

.support-whatsapp-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #25d366;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 22px rgba(7, 94, 42, 0.26);
  z-index: 62;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.support-whatsapp-fab:hover {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 16px 28px rgba(7, 94, 42, 0.3);
}

.support-whatsapp-icon {
  width: 28px;
  height: 28px;
  display: block;
  fill: currentColor;
}

.support-whatsapp-tooltip {
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%) translateX(4px);
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.support-whatsapp-fab:hover .support-whatsapp-tooltip {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.escalation-toast__title {
  font-size: 0.88rem;
  font-weight: 700;
}

.escalation-toast__subtitle {
  margin-top: 2px;
  font-size: 0.78rem;
  color: #9f1239;
}

.escalation-toast__actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.escalation-toast__btn {
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 10px;
}

.escalation-toast__btn--primary {
  background: #be123c;
  color: white;
}

.escalation-toast__btn--primary:hover {
  background: #9f1239;
}

.escalation-toast__btn--ghost {
  border-color: #fda4af;
  background: #ffe4e6;
  color: #9f1239;
}

.escalation-toast__btn--ghost:hover {
  background: #fecdd3;
}

.escalation-toast-enter-active,
.escalation-toast-leave-active {
  transition: all 0.2s ease;
}

.escalation-toast-enter-from,
.escalation-toast-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Dark via clase .dark (tema manual o inicialización del composable) */
:global(.dark .layout-broadcast-banner--info) {
  background: rgb(37 99 235 / 0.12);
  border-color: rgb(96 165 250 / 0.3);
}
:global(.dark .layout-broadcast-banner--warning) {
  background: rgb(161 98 7 / 0.15);
  border-color: rgb(251 191 36 / 0.3);
}
:global(.dark .layout-broadcast-banner--critical) {
  background: rgb(185 28 28 / 0.15);
  border-color: rgb(248 113 113 / 0.3);
}
:global(.dark .layout-broadcast-banner__icon--info)     { color: #60a5fa; }
:global(.dark .layout-broadcast-banner__icon--warning)  { color: #fbbf24; }
:global(.dark .layout-broadcast-banner__icon--critical) { color: #f87171; }

/* Dark via preferencia del SO (cubre el instante antes de que el JS añada .dark) */
@media (prefers-color-scheme: dark) {
  :global(:root:not(.light) .layout-broadcast-banner--info) {
    background: rgb(37 99 235 / 0.12);
    border-color: rgb(96 165 250 / 0.3);
  }
  :global(:root:not(.light) .layout-broadcast-banner--warning) {
    background: rgb(161 98 7 / 0.15);
    border-color: rgb(251 191 36 / 0.3);
  }
  :global(:root:not(.light) .layout-broadcast-banner--critical) {
    background: rgb(185 28 28 / 0.15);
    border-color: rgb(248 113 113 / 0.3);
  }
  :global(:root:not(.light) .layout-broadcast-banner__icon--info)     { color: #60a5fa; }
  :global(:root:not(.light) .layout-broadcast-banner__icon--warning)  { color: #fbbf24; }
  :global(:root:not(.light) .layout-broadcast-banner__icon--critical) { color: #f87171; }
}

:global(.dark .escalation-toast) {
  background: rgb(136 19 55 / 0.2);
  border-color: rgb(190 24 93 / 0.45);
  color: #fecdd3;
}

:global(.dark .escalation-toast__subtitle) {
  color: #fda4af;
}

:global(.dark .escalation-toast__btn--primary) {
  background: #be123c;
}

:global(.dark .escalation-toast__btn--ghost) {
  border-color: rgb(251 113 133 / 0.55);
  background: rgb(190 24 93 / 0.18);
  color: #fecdd3;
}

:global(.dark .impersonation-banner) {
  background: rgb(120 53 15 / 0.4);
  color: #fcd34d;
  border-bottom-color: rgb(180 83 9 / 0.5);
}

:global(.dark .impersonation-banner__btn) {
  background: rgb(217 119 6 / 0.35);
  border-color: rgb(217 119 6 / 0.6);
  color: #fde68a;
}

:global(.dark .support-whatsapp-tooltip) {
  background: #111827;
  color: #f9fafb;
  border-color: #374151;
}

@media (max-width: 1023px) {
  .impersonation-banner {
    padding: 8px 10px;
    font-size: 0.72rem;
    align-items: flex-start;
    flex-direction: column;
  }

  .impersonation-spacer {
    height: 56px;
  }

  .support-whatsapp-fab {
    right: 16px;
    bottom: 16px;
  }

  .escalation-toast-stack {
    right: 16px;
  }

  .escalation-toast-stack--with-support {
    bottom: 84px;
  }
}

@keyframes studioSwitchSpin {
  to { transform: rotate(360deg); }
}
</style>
